import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UserState } from "@/context/User/UserReducer";
import {
	addActionType,
	AddActionTypeParams,
} from "@/services/ActionType.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";

const formSchema = z.object({
	actiontype: z.string(),
});

const CreateActionType: React.FC<{ user: UserState | null }> = ({ user }) => {
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const createActionType = useMutation({
		mutationKey: ["actiontype_create"],
		mutationFn: (data: AddActionTypeParams) =>
			addActionType(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["event_types"],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `Event type ${variables.actiontype} has been successfully created`,
			});
			createActionType.reset();
		},
		onError: (err) => {
			toast.error("Error!", {
				description: "Error adding event type, try again",
			});
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		console.log(data);
		createActionType.mutate(data);
	};

	const disabled = () => {
		if (createActionType.isPending) return true;
		if (createActionType.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Add Event Type</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Add Event Type
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="actiontype"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Event Type</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={createActionType.isPending || createActionType.isSuccess}
						>
							{createActionType.isPending ? "Creating..." : "Create Event Type"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default CreateActionType;
